/**
 * interact/actions.ts — The Action Vocabulary
 *
 * Every piece of furniture in Hearth can be touched, sat in, lit, played,
 * watered, listened to. This module defines what each of those touches does —
 * the verbs of a living room.
 *
 * An InteractionAction is a small, self-contained unit of meaning: a label,
 * an icon, and a function that takes the world and a piece of furniture and
 * returns what just happened. Clean data. No side effects beyond the state
 * you're handed.
 *
 * The messages here are deliberately gentle. "You water the plant." Not
 * "PLANT WATERED." Not a toast notification. A whisper, the kind of thing
 * a room might tell you if it could speak.
 */

import type { GameState, FurniturePlacement, Direction } from "../engine/state.js";
import { wallSnappedPosition } from "../room/furniture.js";

/**
 * Map furniture direction to the correct player sprite direction for sitting.
 *
 * Isometric projection rotates the world 45° relative to the screen. A chair
 * facing "W" in world space points northwest on screen, which corresponds to
 * the "NW" player sprite direction. Each furniture direction shifts one step
 * clockwise on the compass to get the matching character sprite.
 */
const FURNITURE_TO_PLAYER_DIR: Record<Direction, Direction> = {
  "N": "NE", "NE": "E", "E": "SE", "SE": "S",
  "S": "SW", "SW": "W", "W": "NW", "NW": "N",
};
function furnitureDirToPlayerDir(dir: Direction): Direction {
  return FURNITURE_TO_PLAYER_DIR[dir] ?? dir;
}

// ─── Result & Action Types ────────────────────────────────────────────────────

/**
 * What happened when a player interacted with a piece of furniture.
 * The game loop reads this and decides what to play, what to animate,
 * what notification to show.
 */
export interface InteractionResult {
  success: boolean;
  /** Notification text — shown gently, not shouted */
  message?: string;
  /** Sound key for the audio engine */
  sound?: string;
  /** Character animation to play */
  playerAnim?: string;
  /** Direction for the character to face */
  playerDir?: string;
}

/**
 * A single action a player can take on a piece of furniture.
 *
 * `icon` is a single character: a letter, a symbol, whatever fits.
 * It's rendered in the tooltip and (eventually) in the action wheel.
 */
export interface InteractionAction {
  type: string;
  label: string;
  icon: string;
  execute: (state: GameState, furniture: FurniturePlacement) => InteractionResult;
}

// ─── Observation Pool ────────────────────────────────────────────────────────

/** Gentle observations for things you can only look at, not touch. */
const OBSERVE_MESSAGES = [
  "The fish seem happy.",
  "The crystal glows faintly.",
  "You gaze for a moment...",
  "There's something calming about watching.",
  "Time slows here, just a little.",
  "You could stand here all day.",
];

function pickObservation(): string {
  return OBSERVE_MESSAGES[Math.floor(Math.random() * OBSERVE_MESSAGES.length)];
}

// ─── Action Definitions ───────────────────────────────────────────────────────

/**
 * Toggle a lamp, candle, fireplace, or any on/off furniture.
 * Reads and flips furniture.state.on.
 */
const ACTION_TOGGLE: InteractionAction = {
  type: "toggle",
  label: "Toggle",
  icon: "○",
  execute(_state, furniture) {
    const wasOn = Boolean(furniture.state.on);
    furniture.state.on = !wasOn;
    const label = furniture.type === "floor_lamp" ? "Lamp" : "Light";
    // Use lamp_switch for lamps, toggle_on/off for everything else
    const isLamp = furniture.type?.includes("lamp") || furniture.type?.includes("candle");
    const sound = isLamp
      ? "lamp_switch"
      : wasOn ? "toggle_off" : "toggle_on";
    return {
      success: true,
      message: wasOn ? `${label} turned off.` : `${label} turned on.`,
      sound,
    };
  },
};

/**
 * Sit in a chair, on a bed edge, on a chest.
 * Sets the player's animation and faces them toward the furniture.
 */
const ACTION_SIT: InteractionAction = {
  type: "sit",
  label: "Sit",
  icon: "⌓",
  execute(state, furniture) {
    // Toggle — if already sitting on this furniture, stand up
    if ((state.player.animState === "sit" || state.player.animState === "device_sit") &&
        state.player.position.tileX === furniture.tileX &&
        state.player.position.tileY === furniture.tileY) {
      const standAnim = state.ui.combatStance ? "combat_idle" : "idle";
      state.player.animState = standAnim;
      state.player.position.elevation = 0;
      return {
        success: true,
        sound: "cloth_rustle",
        playerAnim: standAnim,
        playerDir: state.player.direction,
      };
    }
    state.player.animState = "sit";
    // Snap player onto the furniture tile — they sit IN the chair, not beside it
    state.player.position.tileX = furniture.tileX;
    state.player.position.tileY = furniture.tileY;
    state.player.position.elevation = furniture.elevation;
    state.player.moveProgress = 0;
    state.player.targetPosition = undefined;
    state.player.movementQueue = [];
    // Face the direction the seat faces (furniture dir rotated 45° for isometric)
    state.player.direction = furnitureDirToPlayerDir(furniture.direction as Direction);
    return {
      success: true,
      sound: "chair_creak",
      playerAnim: "sit",
      playerDir: state.player.direction,
    };
  },
};

/**
 * Sleep in a bed.
 * Like sit, but longer and quieter.
 */
const ACTION_SLEEP: InteractionAction = {
  type: "sleep",
  label: "Sleep",
  icon: "☽",
  execute(state, furniture) {
    // Toggle — if already sleeping on this furniture, wake up
    if (state.player.animState === "sleep" &&
        state.player.position.tileX === furniture.tileX &&
        state.player.position.tileY === furniture.tileY) {
      const standAnim = state.ui.combatStance ? "combat_idle" : "idle";
      state.player.animState = standAnim;
      state.player.position.elevation = 0;
      return { success: true, sound: "cloth_rustle", playerAnim: standAnim, playerDir: state.player.direction };
    }
    state.player.animState = "sleep";
    // Snap player onto the bed
    state.player.position.tileX = furniture.tileX;
    state.player.position.tileY = furniture.tileY;
    state.player.position.elevation = furniture.elevation;
    state.player.moveProgress = 0;
    state.player.targetPosition = undefined;
    state.player.movementQueue = [];
    state.player.direction = furnitureDirToPlayerDir(furniture.direction as Direction);
    return {
      success: true,
      message: "Resting...",
      sound: "cloth_settle",
      playerAnim: "sleep",
      playerDir: state.player.direction,
    };
  },
};

/**
 * Browse a bookshelf.
 * No state change — just the pleasure of looking.
 */
const ACTION_READ: InteractionAction = {
  type: "read",
  label: "Browse",
  icon: "📖",
  execute(state, _furniture) {
    state.player.animState = "interact";
    return {
      success: true,
      message: "You browse the shelves...",
      sound: "bookshelf_rustle",
      playerAnim: "interact",
    };
  },
};

/**
 * Water a plant.
 * Increments waterLevel by 0.3, capped at 1.
 * Gently refuses if already saturated.
 */
const ACTION_WATER: InteractionAction = {
  type: "water",
  label: "Water",
  icon: "~",
  execute(state, furniture) {
    const current = typeof furniture.state.waterLevel === "number"
      ? furniture.state.waterLevel
      : 0;

    if (current >= 1) {
      return {
        success: false,
        message: "Already well watered.",
        sound: "water_drip",
      };
    }

    furniture.state.waterLevel = Math.min(1, current + 0.3);
    state.player.animState = "interact";

    return {
      success: true,
      message: "You water the plant.",
      sound: "water_pour",
      playerAnim: "interact",
    };
  },
};

/**
 * Open the Aeterna interface.
 * Sets ui.aeternaOpen = true. The Aeterna knows who you are.
 */
const ACTION_AETERNA: InteractionAction = {
  type: "aeterna",
  label: "Speak",
  icon: "A",
  execute(state, _furniture) {
    state.ui.aeternaOpen = true;
    return {
      success: true,
      message: "Aeterna awakens.",
      sound: "toggle_on",
    };
  },
};

/**
 * Toggle music on a record player or similar.
 * Flips furniture.state.playing.
 */
const ACTION_MUSIC: InteractionAction = {
  type: "music",
  label: "Play",
  icon: "♪",
  execute(_state, furniture) {
    const wasPlaying = Boolean(furniture.state.playing);
    furniture.state.playing = !wasPlaying;
    return {
      success: true,
      message: wasPlaying ? "Music fades." : "Music fills the room.",
      sound: wasPlaying ? "toggle_off" : "needle_drop",
    };
  },
};

/**
 * Play the piano.
 * Sets the player into an interact animation. Sound handled by audio system.
 */
const ACTION_PIANO: InteractionAction = {
  type: "piano",
  label: "Play",
  icon: "♬",
  execute(state, furniture) {
    state.player.animState = "interact";
    const dx = furniture.tileX - state.player.position.tileX;
    const dy = furniture.tileY - state.player.position.tileY;
    state.player.direction = directionFromDelta(dx, dy);
    return {
      success: true,
      message: "You play a few notes...",
      sound: "piano",
      playerAnim: "interact",
      playerDir: state.player.direction,
    };
  },
};

/**
 * Observe something — fish tank, fountain, crystal ball.
 * A moment of stillness. Random message from the observation pool.
 */
const ACTION_OBSERVE: InteractionAction = {
  type: "observe",
  label: "Look",
  icon: "◎",
  execute(state, furniture) {
    state.player.animState = "interact";
    const dx = furniture.tileX - state.player.position.tileX;
    const dy = furniture.tileY - state.player.position.tileY;
    state.player.direction = directionFromDelta(dx, dy);
    return {
      success: true,
      message: pickObservation(),
      sound: "glass_tap",
      playerAnim: "interact",
      playerDir: state.player.direction,
    };
  },
};

/**
 * Cook at a stove.
 * The player turns to face it and gets to work.
 */
const ACTION_COOKING: InteractionAction = {
  type: "cooking",
  label: "Cook",
  icon: "⋮",
  execute(state, furniture) {
    state.player.animState = "interact";
    const dx = furniture.tileX - state.player.position.tileX;
    const dy = furniture.tileY - state.player.position.tileY;
    state.player.direction = directionFromDelta(dx, dy);
    return {
      success: true,
      message: "You start cooking...",
      sound: "fire_ignite",
      playerAnim: "interact",
      playerDir: state.player.direction,
    };
  },
};

/**
 * Play an arcade cabinet or other game furniture.
 */
const ACTION_GAME: InteractionAction = {
  type: "game",
  label: "Play",
  icon: "▶",
  execute(state, furniture) {
    state.player.animState = "interact";
    const dx = furniture.tileX - state.player.position.tileX;
    const dy = furniture.tileY - state.player.position.tileY;
    state.player.direction = directionFromDelta(dx, dy);
    return {
      success: true,
      message: "Game on!",
      sound: "arcade_coin",
      playerAnim: "interact",
      playerDir: state.player.direction,
    };
  },
};

/**
 * Sit at a chess table.
 * You study the board. The board studies you back.
 */
const ACTION_CHESS: InteractionAction = {
  type: "chess",
  label: "Study",
  icon: "♟",
  execute(state, furniture) {
    state.ui.combatStance = false; // sitting cancels combat stance
    state.player.animState = "sit";
    const dx = furniture.tileX - state.player.position.tileX;
    const dy = furniture.tileY - state.player.position.tileY;
    state.player.direction = directionFromDelta(dx, dy);
    return {
      success: true,
      message: "You study the board...",
      sound: "chess_piece",
      playerAnim: "sit",
      playerDir: state.player.direction,
    };
  },
};

/**
 * Work at a desk — typing, writing, thinking.
 * A gentle transition into focus.
 */
const ACTION_WORK: InteractionAction = {
  type: "work",
  label: "Work",
  icon: "✎",
  execute(state, furniture) {
    state.ui.combatStance = false; // working cancels combat stance
    state.player.animState = "type";
    const dx = furniture.tileX - state.player.position.tileX;
    const dy = furniture.tileY - state.player.position.tileY;
    state.player.direction = directionFromDelta(dx, dy);
    return {
      success: true,
      message: "You settle in to work.",
      sound: "chair_creak",
      playerAnim: "type",
      playerDir: state.player.direction,
    };
  },
};

/**
 * Use a computer — similar to work, with a different tone.
 */
const ACTION_COMPUTER: InteractionAction = {
  type: "computer",
  label: "Use",
  icon: "▢",
  execute(state, furniture) {
    state.ui.combatStance = false; // using computer cancels combat stance
    state.player.animState = "type";
    const dx = furniture.tileX - state.player.position.tileX;
    const dy = furniture.tileY - state.player.position.tileY;
    state.player.direction = directionFromDelta(dx, dy);
    return {
      success: true,
      message: "Screen glow fills the room.",
      sound: "toggle_on",
      playerAnim: "type",
      playerDir: state.player.direction,
    };
  },
};

/**
 * Open a chest or storage.
 */
const ACTION_STORAGE: InteractionAction = {
  type: "storage",
  label: "Open",
  icon: "▤",
  execute(state, furniture) {
    state.player.animState = "interact";
    const dx = furniture.tileX - state.player.position.tileX;
    const dy = furniture.tileY - state.player.position.tileY;
    state.player.direction = directionFromDelta(dx, dy);
    const wasOpen = Boolean(furniture.state.open);
    furniture.state.open = !wasOpen;
    return {
      success: true,
      message: wasOpen ? "You close the chest." : "You open the chest.",
      sound: wasOpen ? "chest_close" : "chest_open",
      playerAnim: "interact",
      playerDir: state.player.direction,
    };
  },
};

/**
 * Bookshelf — browse the shelves, same feeling as read.
 */
const ACTION_BOOKSHELF: InteractionAction = {
  type: "bookshelf",
  label: "Browse",
  icon: "📖",
  execute(state, furniture) {
    state.player.animState = "interact";
    const dx = furniture.tileX - state.player.position.tileX;
    const dy = furniture.tileY - state.player.position.tileY;
    state.player.direction = directionFromDelta(dx, dy);
    return {
      success: true,
      message: "You browse the shelves...",
      sound: "bookshelf_rustle",
      playerAnim: "interact",
      playerDir: state.player.direction,
    };
  },
};

/**
 * Divine — crystal ball, scrying, mystery.
 * A pause. A question without an answer.
 */
const ACTION_DIVINE: InteractionAction = {
  type: "divine",
  label: "Gaze",
  icon: "✦",
  execute(state, furniture) {
    state.player.animState = "interact";
    const dx = furniture.tileX - state.player.position.tileX;
    const dy = furniture.tileY - state.player.position.tileY;
    state.player.direction = directionFromDelta(dx, dy);
    return {
      success: true,
      message: "The light inside shifts. You don't know what it means.",
      sound: "crystal_hum",
      playerAnim: "interact",
      playerDir: state.player.direction,
    };
  },
};

/**
 * Customize — wall art, personalization.
 * Opens a future customization panel.
 */
const ACTION_CUSTOMIZE: InteractionAction = {
  type: "customize",
  label: "Edit",
  icon: "✏",
  execute(state, furniture) {
    state.player.animState = "interact";
    const dx = furniture.tileX - state.player.position.tileX;
    const dy = furniture.tileY - state.player.position.tileY;
    state.player.direction = directionFromDelta(dx, dy);
    return {
      success: true,
      message: "You step back and consider the piece.",
      sound: "cloth_rustle",
      playerAnim: "interact",
      playerDir: state.player.direction,
    };
  },
};

/**
 * Arcade — same as game but for the cabinet specifically.
 */
const ACTION_ARCADE: InteractionAction = {
  type: "arcade",
  label: "Play",
  icon: "▶",
  execute(state, furniture) {
    state.player.animState = "interact";
    const dx = furniture.tileX - state.player.position.tileX;
    const dy = furniture.tileY - state.player.position.tileY;
    state.player.direction = directionFromDelta(dx, dy);
    return {
      success: true,
      message: "The screen flickers to life.",
      sound: "toggle_on",
      playerAnim: "interact",
      playerDir: state.player.direction,
    };
  },
};

// ─── Action Map ───────────────────────────────────────────────────────────────

/**
 * The master lookup: interactionType string → one or more actions.
 *
 * Most furniture has a single primary action. When multiple actions are
 * returned, the first is the default (used when only one click is needed),
 * and the full list is available for a future context menu.
 */
const ACTION_MAP: Record<string, InteractionAction[]> = {
  toggle:    [ACTION_TOGGLE],
  sit:       [ACTION_SIT],
  sleep:     [ACTION_SLEEP],
  read:      [ACTION_READ],
  bookshelf: [ACTION_BOOKSHELF],
  water:     [ACTION_WATER],
  aeterna:   [ACTION_AETERNA],
  music:     [ACTION_MUSIC],
  piano:     [ACTION_PIANO],
  observe:   [ACTION_OBSERVE],
  cooking:   [ACTION_COOKING],
  game:      [ACTION_GAME],
  chess:     [ACTION_CHESS, ACTION_SIT],  // study the board or just sit
  work:      [ACTION_WORK, ACTION_SIT],   // work or sit
  computer:  [ACTION_COMPUTER],
  storage:   [ACTION_STORAGE],
  divine:    [ACTION_DIVINE],
  customize: [ACTION_CUSTOMIZE],
  arcade:    [ACTION_ARCADE],
};

/**
 * Returns the actions available for a given interactionType.
 *
 * The first action in the list is always the "primary" — the one triggered
 * by a plain click. If the list has more than one, the player can eventually
 * choose from a radial or context menu.
 */
export function getActionsForFurniture(interactionType: string): InteractionAction[] {
  return ACTION_MAP[interactionType] ?? [];
}

// ─── Hint Text ────────────────────────────────────────────────────────────────

/**
 * Returns a short hint string for a given interactionType,
 * suitable for rendering in a tooltip ("Click to sit", "Click to toggle").
 */
export function getHintForInteractionType(interactionType: string): string {
  const HINTS: Record<string, string> = {
    toggle:    "Click to toggle",
    sit:       "Click to sit",
    sleep:     "Click to sleep",
    read:      "Click to browse",
    bookshelf: "Click to browse",
    water:     "Click to water",
    aeterna:   "Click to speak with Aeterna",
    music:     "Click to play music",
    piano:     "Click to play",
    observe:   "Click to look",
    cooking:   "Click to cook",
    game:      "Click to play",
    chess:     "Click to study",
    work:      "Click to work",
    computer:  "Click to use",
    storage:   "Click to open",
    divine:    "Click to gaze",
    customize: "Click to edit",
    arcade:    "Click to play",
  };
  return HINTS[interactionType] ?? "Click to interact";
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Compute an 8-directional facing from a delta vector.
 * Used by actions to face the player toward whatever they're interacting with.
 */
function directionFromDelta(dx: number, dy: number): import("../engine/state.js").Direction {
  if (dx > 0 && dy === 0) return "SE";
  if (dx < 0 && dy === 0) return "NW";
  if (dx === 0 && dy > 0) return "SW";
  if (dx === 0 && dy < 0) return "NE";
  if (dx > 0 && dy > 0) return "S";
  if (dx < 0 && dy < 0) return "N";
  if (dx > 0 && dy < 0) return "E";
  if (dx < 0 && dy > 0) return "W";
  return "S"; // standing still — face south, toward the viewer
}
